/* global APP, interfaceConfig */

import React from 'react';
import { createRoot } from 'react-dom/client';

import UIUtil from '../../../modules/UI/util/UIUtil';
import UIEvents from '../../../service/UI/UIEvents';
import { getActiveDOMDocument, getPictureInPictureModeConfig } from '../base/config/functions.any';
import { isTestModeEnabled } from '../base/testing';
import { getFilmstripHeight, getVerticalFilmstripWidth } from '../filmstrip/functions.web';
import { LAYOUTS, getCurrentLayout, shouldDisplayTileView } from '../video-layout';

import LargeContainer from './LargeContainer';
import { updateLastLargeVideoMediaEvent } from './actions.web';
import { LargeVideoBackground, ORIENTATION } from './components/LargeVideoBackground.web';

// FIXME should be 'video'
export const VIDEO_CONTAINER_TYPE = 'camera';

const FADE_DURATION_MS = 300;

/**
 * List of container events that we are going to process, will be added as listener to the
 * container for every event in the list. The latest event will be stored in redux.
 */
const containerEvents = [
    'abort', 'canplay', 'canplaythrough', 'emptied', 'ended', 'error', 'loadeddata', 'loadedmetadata', 'loadstart',
    'pause', 'play', 'playing', 'ratechange', 'stalled', 'suspend', 'waiting'
];

/**
 * Returns an array of the video dimensions, so that it keeps its aspect
 * ratio and fits available area with its larger dimension. This method
 * ensures that whole video will be visible and can leave empty areas.
 *
 * @param {number} videoWidth - The width of the video to position.
 * @param {number} videoHeight - The height of the video to position.
 * @param {number} videoSpaceWidth - The width of the available space.
 * @param {number} videoSpaceHeight - The height of the available space.
 * @returns {Array<number>} An array with 2 elements: the video width and the video height.
 */
export function computeDesktopVideoSize(
        videoWidth,
        videoHeight,
        videoSpaceWidth,
        videoSpaceHeight) {
    if (videoWidth === 0 || videoHeight === 0 || videoSpaceWidth === 0 || videoSpaceHeight === 0) {
        // Avoid NaN values caused by division by 0.
        return [ 0, 0 ];
    }

    const aspectRatio = videoWidth / videoHeight;
    let availableWidth = Math.max(videoWidth, videoSpaceWidth);
    let availableHeight = Math.max(videoHeight, videoSpaceHeight);

    let effectiveSpaceWidth = videoSpaceWidth;
    let effectiveSpaceHeight = videoSpaceHeight;

    if (typeof interfaceConfig !== 'undefined' && interfaceConfig.VERTICAL_FILMSTRIP) {
        effectiveSpaceWidth -= getVerticalFilmstripWidth();
    } else {
        effectiveSpaceHeight -= getFilmstripHeight();
    }

    if (availableWidth / aspectRatio >= effectiveSpaceHeight) {
        availableHeight = effectiveSpaceHeight;
        availableWidth = availableHeight * aspectRatio;
    }

    if (availableHeight * aspectRatio >= effectiveSpaceWidth) {
        availableWidth = effectiveSpaceWidth;
        availableHeight = availableWidth / aspectRatio;
    }

    return [ availableWidth, availableHeight ];
}

/**
 * Returns an array of the video dimensions. It respects the
 * VIDEO_LAYOUT_FIT config, to fit the video to the screen, by hiding some parts
 * of it, or to fit it to the height or width.
 *
 * @param {number} videoWidth - The original video width.
 * @param {number} videoHeight - The original video height.
 * @param {Object} options - Video space options.
 * @returns {Array<number>} An array with 2 elements: the video width and the video height.
 */
export function computeCameraVideoSize(
        videoWidth,
        videoHeight,
        { videoSpaceWidth,
            videoSpaceHeight,
            videoLayoutFit }) {
    if (videoWidth === 0 || videoHeight === 0 || videoSpaceWidth === 0 || videoSpaceHeight === 0) {
        // Avoid NaN values caused by division by 0.
        return [ 0, 0 ];
    }

    const aspectRatio = videoWidth / videoHeight;

    switch (videoLayoutFit) {
    case 'height':
        return [ videoSpaceHeight * aspectRatio, videoSpaceHeight ];
    case 'width':
        return [ videoSpaceWidth, videoSpaceWidth / aspectRatio ];
    case 'both': {
        const videoSpaceRatio = videoSpaceWidth / videoSpaceHeight;
        const maxZoomCoefficient = (typeof interfaceConfig !== 'undefined' && interfaceConfig.MAXIMUM_ZOOMING_COEFFICIENT)
                || Infinity;

        if (videoSpaceRatio === aspectRatio) {
            return [ videoSpaceWidth, videoSpaceHeight ];
        }

        let [ width, height ] = computeCameraVideoSize(
                videoWidth,
                videoHeight,
                { videoSpaceWidth,
                    videoSpaceHeight,
                    videoLayoutFit: videoSpaceRatio < aspectRatio ? 'height' : 'width' });
        const maxWidth = videoSpaceWidth * maxZoomCoefficient;
        const maxHeight = videoSpaceHeight * maxZoomCoefficient;

        if (width > maxWidth) {
            width = maxWidth;
            height = width / aspectRatio;
        } else if (height > maxHeight) {
            height = maxHeight;
            width = height * aspectRatio;
        }

        return [ width, height ];
    }
    default:
        return [ videoWidth, videoHeight ];
    }
}

/**
 * Returns an array of the video horizontal and vertical indents,
 * so that it fits its parent.
 *
 * @param {number} videoWidth - The video width.
 * @param {number} videoHeight - The video height.
 * @param {number} videoSpaceWidth - The space width.
 * @param {number} videoSpaceHeight - The space height.
 * @returns {{horizontalIndent: number, verticalIndent: number}}
 */
export function getCameraVideoPosition(
        videoWidth,
        videoHeight,
        videoSpaceWidth,
        videoSpaceHeight) {
    // Parent height isn't completely calculated when we position the video in
    // full screen mode and this is why we use the screen height in this case.
    const effectiveSpaceHeight = UIUtil.isFullScreen()
        ? window.innerHeight
        : videoSpaceHeight;

    const horizontalIndent = (videoSpaceWidth - videoWidth) / 2;
    const verticalIndent = (effectiveSpaceHeight - videoHeight) / 2;

    return {
        horizontalIndent,
        verticalIndent
    };
}

/**
 * Container for user video.
 */
export class VideoContainer extends LargeContainer {
    /**
     *
     */
    get $video() {
        const state = APP.store.getState();

        return getActiveDOMDocument(state, '#largeVideo');
    }

    /**
     * Live lookup: React LargeVideo remounts #largeVideoWrapper (visitor join,
     * leaving the Loading stub, layout switches). Caching the jQuery object in
     * the constructor left show()/hide() animating a detached node, so the new
     * video element stayed visibility:hidden with no track on stage.
     *
     * @returns {Object} jQuery wrapper for #largeVideoWrapper.
     */
    get $wrapper() {
        return getActiveDOMDocument(APP.store.getState(), '#largeVideoWrapper');
    }

    /**
     * Parent of {@link $wrapper}, used for fade show/hide.
     *
     * @returns {Object} jQuery parent, or an empty set when the wrapper is gone.
     */
    get $wrapperParent() {
        const $wrapper = this.$wrapper;

        return $wrapper && typeof $wrapper.parent === 'function' ? $wrapper.parent() : $wrapper;
    }

    /**
     *
     */
    get id() {
        return this.userId;
    }

    /**
     * Creates new VideoContainer instance.
     *
     * @param {Function} resizeContainer - Function that takes care of the size
     * of the video container.
     * @param {Object} emitter - The event emitter that will be used by
     * this instance.
     */
    constructor(resizeContainer, emitter) {
        super();
        this.stream = null;
        this.userId = null;
        this.videoType = null;
        const settings = APP.store?.getState?.()['features/base/settings'];

        this.localFlipX = settings && typeof settings.localFlipX !== 'undefined' ? Boolean(settings.localFlipX) : true;
        this.emitter = emitter;
        this.resizeContainer = resizeContainer;
        this._backgroundRoot = null;

        // The container element _backgroundRoot renders into, and the <video> this.stream was attached to - React can
        // replace either, so both are compared against the current DOM rather than assumed.
        this._backgroundContainer = null;
        this._streamElement = undefined;

        /**
         * Whether the background should fit the height of the container
         * (portrait) or fit the width of the container (landscape).
         *
         * @private
         * @type {string|null}
         */
        this._backgroundOrientation = null;

        /**
         * Flag indicates whether or not the background should be rendered.
         * If the background will not be visible then it is hidden to save
         * on performance.
         *
         * @type {boolean}
         */
        this._hideBackground = true;

        this._isHidden = false;

        /**
         * Flag indicates whether or not the avatar is currently displayed.
         *
         * @type {boolean}
         */
        this.avatarDisplayed = null;
        const state = APP.store.getState();

        this.$avatar = getActiveDOMDocument(state, '#dominantSpeaker');

        /**
         * A jQuery selector of the remote connection message.
         *
         * @type {jQuery|HTMLElement}
         */
        this.$remoteConnectionMessage = getActiveDOMDocument(state, '#remoteConnectionMessage');

        this.$remotePresenceMessage = getActiveDOMDocument(state, '#remotePresenceMessage');

        this.avatarHeight = getActiveDOMDocument(state, '#dominantSpeakerAvatarContainer').height();

        // Add safety check to prevent crashes during room switching
        if (this.$video[0] && typeof this.$video[0].onplaying !== 'undefined') {
            this.$video[0].onplaying = function(event) {
                if (typeof resizeContainer === 'function') {
                    resizeContainer(event);
                }
            };
        }

        /**
         * A Set of functions to invoke when the video element resizes.
         *
         * @private
         */
        this._resizeListeners = new Set();

        // Add safety check to prevent crashes when video element doesn't exist
        if (this.$video[0]) {
            this.$video[0].onresize = this._onResize.bind(this);
        }

        if (isTestModeEnabled(APP.store.getState())) {
            const cb = name => APP.store.dispatch(updateLastLargeVideoMediaEvent(name));

            containerEvents.forEach(event => {
                // Add safety check for video element before adding event listeners
                if (this.$video[0]) {
                    this.$video[0].addEventListener(event, cb.bind(this, event));
                }
            });
        }
    }

    /**
     * Adds a function to the known subscribers of video element resize
     * events.
     *
     * @param {Function} callback - The subscriber to notify when the video
     * element resizes.
     * @returns {void}
     */
    addResizeListener(callback) {
        this._resizeListeners.add(callback);
    }

    /**
     * Obtains media stream ID of the underlying {@link MHTrack}.
     *
     * @returns {string|null}
     */
    getStreamID() {
        return this.stream ? this.stream.getId() : null;
    }

    /**
     * Get size of video element.
     *
     * @returns {{width: number, height: number}}
     */
    getStreamSize() {
        const video = this.$video ? this.$video[0] : null;

        if (!video) {
            return {
                width: 0,
                height: 0
            };
        }

        return {
            width: video.videoWidth || 0,
            height: video.videoHeight || 0
        };
    }

    /**
     * Calculate optimal video size for specified container size.
     *
     * @param {number} containerWidth - Container width.
     * @param {number} containerHeight - Container height.
     * @returns {Array<number>}
     */
    _getVideoSize(containerWidth, containerHeight) {
        const { width, height } = this.getStreamSize();

        if (this.stream && this.isScreenSharing()) {
            return computeDesktopVideoSize(width,
                height,
                containerWidth,
                containerHeight);
        }

        return computeCameraVideoSize(width,
            height,
            { videoSpaceWidth: containerWidth,
                videoSpaceHeight: containerHeight,
                videoLayoutFit: interfaceConfig.VIDEO_LAYOUT_FIT });
    }

    /**
     * Calculate optimal video position (offset for top left corner)
     * for specified video size and container size.
     *
     * @param {number} width - Video width.
     * @param {number} height - Video height.
     * @param {number} containerWidth - Container width.
     * @param {number} containerHeight - Container height.
     * @returns {{horizontalIndent: number, verticalIndent: number}}
     */
    getVideoPosition(width, height, containerWidth, containerHeight) {
        let containerWidthToUse = containerWidth;

        if (this.stream && this.isScreenSharing()) {
            if (typeof interfaceConfig !== 'undefined' && interfaceConfig.VERTICAL_FILMSTRIP) {
                containerWidthToUse -= getVerticalFilmstripWidth();
            }

            return getCameraVideoPosition(width,
                height,
                containerWidthToUse,
                containerHeight);
        }

        return getCameraVideoPosition(width,
            height,
            containerWidthToUse,
            containerHeight);
    }

    /**
     * Updates the positioning of the remote connection presence message and the
     * connection status message which describes that the remote user is having
     * connectivity issues.
     *
     * @returns {void}
     */
    positionRemoteStatusMessages() {
        this._positionParticipantStatus(this.$remoteConnectionMessage);
        this._positionParticipantStatus(this.$remotePresenceMessage);
    }

    /**
     * Modifies the position of the passed in jQuery object so it displays
     * in the middle of the video container or below the avatar.
     *
     * @param {jQuery|HTMLElement} $element
     * @private
     * @returns {void}
     */
    _positionParticipantStatus($element) {
        if (this.avatarDisplayed) {
            const state = APP.store.getState();
            const $avatarImage = getActiveDOMDocument(state, '#dominantSpeakerAvatarContainer');
            const avatarOffset = $avatarImage.offset();

            // Add safety check to prevent crashes when avatar offset is null
            if (avatarOffset && avatarOffset.top !== undefined) {
                $element.css(
                    'top',
                    avatarOffset.top + $avatarImage.height() + 10);
            } else {
                // Fallback to center positioning if avatar offset is not available
                const height = $element.height();
                const parentHeight = $element.parent().height();

                $element.css('top', (parentHeight / 2) - (height / 2));
            }
        } else {
            const height = $element.height();
            const parentHeight = $element.parent().height();

            $element.css('top', (parentHeight / 2) - (height / 2));
        }
    }

    /**
     *
     */
    resize(containerWidth, containerHeight, animate = false) {
        // Prevent TypeError when the Web browser does not support WebRTC (yet).
        if (this.$video.length === 0) {
            return;
        }
        const currentLayout = getCurrentLayout(APP.store.getState());

        if (currentLayout === LAYOUTS.TILE_VIEW && shouldDisplayTileView(APP.store.getState())) {
            // We don't need to resize the large video since it won't be displayed and we'll resize when returning back
            // to stage view (only skip when tile view is actually active).
            return;
        }

        this.positionRemoteStatusMessages();

        const [ width, height ] = this._getVideoSize(containerWidth, containerHeight);

        if (width === 0 || height === 0) {
            // We don't need to set 0 for width or height since the visibility is controlled by the visibility css prop
            // on the largeVideoElementsContainer. Also if the width/height of the video element is 0 the attached
            // stream won't be played.
            return;
        }

        if ((containerWidth > width) || (containerHeight > height)) {
            this._backgroundOrientation = containerWidth > width ? ORIENTATION.LANDSCAPE : ORIENTATION.PORTRAIT;
            this._hideBackground = false;
        } else {
            this._hideBackground = true;
        }

        this._updateBackground();

        const { horizontalIndent, verticalIndent }
            = this.getVideoPosition(width, height, containerWidth, containerHeight);

        this.$wrapper.animate({
            width,
            height,

            top: verticalIndent,
            bottom: verticalIndent,

            left: horizontalIndent,
            right: horizontalIndent
        }, {
            queue: false,
            duration: animate ? 500 : 0
        });
    }

    /**
     * Removes a function from the known subscribers of video element resize
     * events.
     *
     * @param {Function} callback - The callback to remove from known
     * subscribers of video resize events.
     * @returns {void}
     */
    removeResizeListener(callback) {
        this._resizeListeners.delete(callback);
    }

    /**
     * Update video stream.
     *
     * @param {string} userID
     * @param {Object?} stream - New stream.
     * @param {string} videoType - Video type.
     */
    setStream(userID, stream, videoType) {
        this.userId = userID;

        // Visitor join (and the Loading stub) can run this before React has
        // committed #largeVideo. Attach on the next frame once the element exists.
        if (stream && !this.$video[0]) {
            this.stream = stream;
            this.videoType = videoType;
            requestAnimationFrame(() => {
                if (this.userId === userID && this.$video[0] && this.stream === stream) {
                    const current = this.stream;

                    this.stream = null;
                    this.setStream(userID, current, videoType);
                }
            });

            return;
        }

        // The large video's underlying DOM element may not be present yet
        // (e.g. the active DOM document is being (re)mounted, such as after a
        // breakout/room switch). Attaching/detaching a track to an undefined
        // element causes the lib to call addEventListener on undefined and
        // throw. Bail out gracefully; setStream will run again once the
        // element exists.
        const videoElement = this.$video[0];

        // Being asked for the stream this container already has is only a no-op while the element really shows it.
        // `this.stream` is bookkeeping, and the element can move on without it: PiP's deferred restore puts back the
        // srcObject from when the app root changed document - for the main page that pass waits for the tab to be
        // visible again, by which time the stage has switched speakers - and a React remount of the large video hands
        // us a new, empty <video>. Trusting the bookkeeping skipped every later update, so the stage stayed empty over
        // the frozen blur of the previous video while that participant's thumbnail, which does check its element
        // (needsVideoElementReattach in RemoteVideo), kept playing.
        if (this.stream === stream && (!stream?.stream || videoElement?.srcObject === stream.stream)) {
            // Handles the use case for the remote participants when the
            // videoType is received with delay after turning on/off the
            // desktop sharing.
            if (this.videoType !== videoType) {
                this.videoType = videoType;
                this.resizeContainer();
            }

            return;
        }

        if (!videoElement) {
            return;
        }

        // detach old stream - from the element it was attached to as well, if that has since been replaced
        if (this.stream) {
            this._streamElement && this._streamElement !== videoElement && this.stream.detach(this._streamElement);
            this.stream.detach(videoElement);
        }
        this._streamElement = stream ? videoElement : undefined;

        this.stream = stream;
        this.videoType = videoType;

        if (!stream) {
            // The srcObject of the large video element has just been cleared.
            // Notify the external API so that consumers like the Electron
            // AlwaysOnTop (PiP) window can re-read the element and re-bind
            // (or drop) the underlying MediaStream.
            this._notifyLargeVideoStreamChanged(userID);

            return;
        }

        stream.attach(videoElement);

        // In PiP mode, explicitly ensure the video element plays.
        // The browser may block autoplay in the PiP window context.
        const state = APP.store.getState();

        if (getPictureInPictureModeConfig(state) && this.$video[0]) {
            this.$video[0].play().catch(() => { /* autoplay blocked - ignore */ });
        }

        const settings = APP.store?.getState?.()['features/base/settings'];

        if (settings && typeof settings.localFlipX !== 'undefined') {
            this.localFlipX = Boolean(settings.localFlipX);
        }

        this.$video.css({
            transform: this._isFlipX() ? 'scaleX(-1)' : 'none'
        });

        this._updateBackground();

        // Notify the external API that the large video element's srcObject
        // has been swapped.
        this._notifyLargeVideoStreamChanged(userID);
    }

    /**
     * Fires an on-stage-participant-changed notification to the external API
     * so downstream consumers (e.g. the Electron AlwaysOnTop PiP window) can
     * re-read the current srcObject of the large video element.
     *
     * @param {string} userID - The id of the participant currently shown on
     * the large video.
     * @private
     * @returns {void}
     */
    _notifyLargeVideoStreamChanged(userID) {
        try {
            if (typeof APP !== 'undefined'
                    && APP.API
                    && typeof APP.API.notifyOnStageParticipantChanged === 'function'
                    && userID) {
                APP.API.notifyOnStageParticipantChanged(userID);
            }
        } catch {
            // No-op: never let a notification failure break the large video update pipeline.
        }
    }

    /**
     * Changes the flipX state of the local video.
     *
     * @param {boolean} val - True if flipped.
     */
    setLocalFlipX(val) {
        this.localFlipX = val;
        if (!this.$video || !this.stream || !this.stream.isLocal()) {
            return;
        }
        this.$video.css({
            transform: this._isFlipX() ? 'scaleX(-1)' : 'none'
        });

        this._updateBackground();
    }

    /**
     * Whether the video on stage is mirrored: only the local camera, and only while flipping is on.
     *
     * A screenshare never is. setStream() already applied that rule, but setLocalFlipX() did not - and it runs for
     * every local camera change, because LocalVideo.changeVideo() re-announces the flip setting whenever the camera
     * track is swapped (turned off or on, device switch, virtual background). So turning the camera off while the
     * user's own screenshare was on stage mirrored the screenshare.
     *
     * @private
     * @returns {boolean}
     */
    _isFlipX() {
        const streamVideoType = this.stream?.getVideoType ? this.stream.getVideoType() : this.stream?.videoType;
        const videoType = streamVideoType || this.videoType;

        return Boolean(this.stream?.isLocal() && this.localFlipX && videoType === 'camera');
    }


    /**
     * Check if current video stream is screen sharing.
     *
     * @returns {boolean}
     */
    isScreenSharing() {
        return this.videoType === 'desktop';
    }

    /**
     * Show or hide user avatar.
     *
     * @param {boolean} show
     */
    showAvatar(show) {
        if (this.avatarDisplayed === show) {
            return;
        }
        this.$avatar.css('visibility', show ? 'visible' : 'hidden');
        this.avatarDisplayed = show;

        this.emitter.emit(UIEvents.LARGE_VIDEO_AVATAR_VISIBLE, show);
        if (typeof APP !== 'undefined' && APP.API && typeof APP.API.notifyLargeVideoVisibilityChanged === 'function') {
            APP.API.notifyLargeVideoVisibilityChanged(show);
        }
    }

    /**
     * We are doing fadeOut/fadeIn animations on parent div which wraps
     * largeVideo.
     *
     * @returns {Promise}
     */
    show() {
        const state = APP.store.getState();
        const isPiP = getPictureInPictureModeConfig(state);

        if (!this.$wrapperParent || !this.$wrapperParent.length) {
            this._isHidden = false;

            return Promise.resolve();
        }

        return new Promise(resolve => {
            if (isPiP) {
                // Skip animation in PiP mode - requestAnimationFrame is
                // throttled in the background main window.
                this.$wrapperParent.css({
                    visibility: 'visible',
                    opacity: 1
                });
                this._isHidden = false;
                this._updateBackground();
                resolve();
            } else {
                this.$wrapperParent.css('visibility', 'visible').fadeTo(
                    FADE_DURATION_MS,
                    1,
                    () => {
                        this._isHidden = false;
                        this._updateBackground();
                        resolve();
                    }
                );
            }
        });
    }

    /**
     * Hides the large video container.
     *
     * @returns {Promise}
     */
    hide() {
        // as the container is hidden/replaced by another container, hide its avatar
        this.showAvatar(false);

        const state = APP.store.getState();
        const isPiP = getPictureInPictureModeConfig(state);

        if (!this.$wrapperParent || !this.$wrapperParent.length) {
            this._isHidden = true;

            return Promise.resolve();
        }

        return new Promise(resolve => {
            if (isPiP) {
                this.$wrapperParent.css({
                    opacity: 0,
                    visibility: 'hidden'
                });
                this._isHidden = true;
                this._updateBackground();
                resolve();
            } else {
                this.$wrapperParent.fadeTo(FADE_DURATION_MS, 0, () => {
                    this.$wrapperParent.css('visibility', 'hidden');
                    this._isHidden = true;
                    this._updateBackground();
                    resolve();
                });
            }
        });
    }

    /**
     * @returns {boolean} Switch on dominant speaker event if on stage.
     */
    stayOnStage() {
        return false;
    }

    /**
     * Callback invoked when the video element changes dimensions.
     *
     * @private
     * @returns {void}
     */
    _onResize() {
        this._resizeListeners.forEach(callback => callback());
    }

    /**
     * Attaches and/or updates a React Component to be used as a background for
     * the large video, to display blurred video and fill up empty space not
     * taken up by the large video.
     *
     * @private
     * @returns {void}
     */
    _updateBackground() {
        if (
            interfaceConfig.DISABLE_VIDEO_BACKGROUND
        ) {
            return;
        }

        const state = APP.store.getState();
        const activeWindow = getActiveDOMDocument(state);
        const backgroundContainer = activeWindow?.getElementById('largeVideoBackgroundContainer')
            || document.getElementById('largeVideoBackgroundContainer');

        if (!backgroundContainer) {
            return;
        }

        // The React root is kept on the container element rather than on this
        // instance: #largeVideoBackgroundContainer outlives VideoContainer,
        // which is rebuilt on every room switch (backstage <-> main) without
        // destroy() ever being called. Creating a second root for the same
        // element is what produced "createRoot() on a container that has
        // already been passed to createRoot()" and the follow-on "content of
        // the root container was removed without using React" warnings.
        if (backgroundContainer._mhBackgroundRoot
                && !backgroundContainer.isConnected) {
            // Detached container: its root can never render again.
            backgroundContainer._mhBackgroundRoot = null;
        }

        if (!backgroundContainer._mhBackgroundRoot) {
            backgroundContainer._mhBackgroundRoot = createRoot(backgroundContainer);
        }

        this._backgroundRoot = backgroundContainer._mhBackgroundRoot;

        this._backgroundRoot.render(
            <LargeVideoBackground
                hidden = { false }
                mirror = { this._isFlipX() }
                orientationFit = { this._backgroundOrientation }
                videoElement = { this.$video && this.$video[0] }
                videoTrack = { this.stream } />
        );
    }

    /**
     * Cleanup method to unmount React components.
     */
    destroy() {
        if (this._backgroundRoot) {
            const state = APP.store.getState();
            const container = getActiveDOMDocument(state)
                ?.getElementById('largeVideoBackgroundContainer');

            this._backgroundRoot.unmount();

            if (container && container._mhBackgroundRoot === this._backgroundRoot) {
                container._mhBackgroundRoot = null;
            }
            this._backgroundRoot = null;
            this._backgroundContainer = null;
        }
    }
}

export default VideoContainer;
